import { Children, cloneElement, isValidElement, useId } from "react";
import type { InputHTMLAttributes, ReactElement, ReactNode, TextareaHTMLAttributes } from "react";
import { useLayoutEffect, useRef } from "react";

import { cn } from "../../lib/utils";

export interface FieldProps {
  readonly label?: string;
  readonly hint?: string;
  readonly error?: string | null;
  readonly required?: boolean;
  readonly children: ReactNode;
  readonly className?: string;
}

/** Wraps a form control with label / hint / error in the same vertical
 *  rhythm. Explicit htmlFor/id association satisfies WCAG 1.3.1; the
 *  axe label check requires id + label[for=id] or aria-label, not just
 *  an implicit wrapping label. */
export function Field({ label, hint, error, required, children, className }: FieldProps) {
  const id = useId();
  // Inject the generated id into the first child element so label[for]
  // has a matching target without callers needing to manage ids.
  const child = Children.only(children);
  const childWithId =
    isValidElement(child) && !(child as ReactElement<{ id?: string }>).props.id
      ? cloneElement(child as ReactElement<{ id?: string }>, { id })
      : child;
  return (
    <div className={cn("block space-y-1.5", className)}>
      {label && (
        <label htmlFor={id} className="block text-sm font-medium text-foreground">
          {label}
          {required && <span className="ml-1 text-red-500">*</span>}
        </label>
      )}
      {childWithId}
      {error ? (
        <span className="block text-xs text-red-600">{error}</span>
      ) : hint ? (
        <span className="block text-xs text-muted">{hint}</span>
      ) : null}
    </div>
  );
}

export interface TextInputProps extends InputHTMLAttributes<HTMLInputElement> {
  readonly invalid?: boolean;
}

/** Standard text input. The polish is in the focus + invalid states
 *  - brand-primary ring on focus, red ring on invalid, and the
 *  shadcn-style 1px ring instead of 2px border so adjacent inputs
 *  don't visually crowd. */
export function TextInput({ invalid, className, ...rest }: TextInputProps) {
  return (
    <input
      className={cn(
        "block w-full rounded-md bg-surface px-3 py-2 text-sm text-strong placeholder:text-faint",
        "ring-1 ring-inset ring-line transition-shadow",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-primary",
        "disabled:cursor-not-allowed disabled:bg-surface-muted disabled:text-muted",
        invalid && "ring-red-300 focus:ring-red-500",
        className,
      )}
      {...rest}
    />
  );
}

export interface TextAreaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  readonly invalid?: boolean;
  /** Auto-grow the textarea to fit its content (no inner scrollbar
   *  while it can fit on screen). Default true. With this on we also
   *  disable manual resize since auto-grow would clobber a hand-set
   *  height on the next keystroke. */
  readonly autoSize?: boolean;
}

/** Multiline counterpart to ``TextInput``. Same visual language -
 *  same ring, same paddings, same focus ring - so a row of mixed
 *  inputs reads consistently. Defaults to ``rows={1}`` so the
 *  collapsed shape matches a single-line input; auto-grow expands
 *  it as the cashier types or when an upstream value (e.g. a
 *  pre-filled vehicle deposit description with embedded newlines)
 *  spans multiple lines. */
export function TextArea({
  invalid,
  className,
  autoSize = true,
  rows = 1,
  value,
  defaultValue,
  ...rest
}: TextAreaProps) {
  const ref = useRef<HTMLTextAreaElement | null>(null);

  // Resize on every value change. ``useLayoutEffect`` avoids the
  // one-frame flash at the wrong height when value comes in async
  // (e.g. ``initialDescription`` from Inventory deep-link).
  useLayoutEffect(() => {
    if (!autoSize) return;
    const el = ref.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  }, [autoSize, value, defaultValue]);

  return (
    <textarea
      ref={ref}
      rows={rows}
      value={value}
      defaultValue={defaultValue}
      className={cn(
        "block w-full rounded-md bg-surface px-3 py-2 text-sm text-strong placeholder:text-faint",
        "ring-1 ring-inset ring-line transition-shadow",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-primary",
        "disabled:cursor-not-allowed disabled:bg-surface-muted disabled:text-muted",
        autoSize ? "resize-none overflow-hidden" : "resize-y",
        invalid && "ring-red-300 focus:ring-red-500",
        className,
      )}
      {...rest}
    />
  );
}
